import { inject, Injectable } from '@angular/core';
import { BaseService } from '../core';
import { BuyerService } from './buyer.service';
import { ProductService } from './product.service';
import { BuyerCompany } from '../dto';

@Injectable({
  providedIn: 'root',
})
export class UDirectService extends BaseService {
  private readonly UDIRECT_REGIONS = ['Europe', 'America'];

  /**
   * Europe Direct 주문 마감 시각 (KST 2026-06-02 23:59)
   * - 이 시각 이후로는 Europe Direct order 버튼을 비활성화한다.
   */
  private static readonly EUROPE_DIRECT_ORDER_DEADLINE = new Date('2026-06-02T23:59:59+09:00').getTime();

  /** U-Direct 사용여부 */
  isUDirect = false;
  /** U-Direct 지역명 */
  uDirectZone = '';
  /** TAX, Busines Registration Number 입력 여부 */
  isTax = false;

  /**
   * Europe Direct 주문 마감 여부
   * - uDirectZone이 'Europe'이고 현재 시각이 마감 시각(2026-06-02 23:59 KST) 이후이면 true
   */
  get isEuropeDirectOrderClosed(): boolean {
    return this.uDirectZone === 'Europe' && Date.now() >= UDirectService.EUROPE_DIRECT_ORDER_DEADLINE;
  }

  private buyerService = inject(BuyerService);
  private productService = inject(ProductService);

  constructor() {
    super();
  }

  /**
   * U-Direct 지역에 따른 라우터 링크 반환
   * - Europe인 경우 'u-direct' 반환
   * - America인 경우 'a-direct' 반환
   */
  getUDirectPath(): string {
    return this.uDirectZone === 'Europe' ? '/u-direct' : '/a-direct';
  }

  /**
   * Product의 U-direct 필터링 타입 반환
   * - Europe인 경우 'europeStock' 반환
   * - America인 경우 'americaStock' 반환
   */
  getUDirectStockType(): 'europeStock' | 'americaStock' {
    return this.uDirectZone === 'Europe' ? 'europeStock' : 'americaStock';
  }

  /**
   * U-Direct 서비스 접근 권한 체크 및 설정
   * - 유저의 지역이 U-Direct 서비스 지역(Europe/America)인지 확인
   * - 해당하는 경우 uDirectZone 설정 (Europe/America)
   * - 해당하지 않는 경우 U-Direct 상품 제거
   */
  checkUDirectAccess(): Promise<boolean> {
    return new Promise((res) => {
      this.buyerService.getBuyer().subscribe((buyer) => {
        const region = buyer.buyerCompany.country.directRegion;
        this.isUDirect = this.UDIRECT_REGIONS.includes(region);

        if (this.isUDirect) {
          this.uDirectZone = region;
        }

        this.productService.removeUdirectSelect(region);
        res(this.isUDirect);
      });
    });
  }

  /**
   * home에서 사용될 buyer 정보
   * @returns BuyerCompany{vatNumber, businessNumber, certificateStatus, rejectedReason}
   */
  async getTaxInfo(): Promise<BuyerCompany> {
    return new Promise((res, rej) => {
      // 상태값, 반려사유, tax번호, eori번호
      this.buyerService.getBuyer().subscribe((buyer) => {
        res(
          new BuyerCompany({
            vatNumber: buyer.buyerCompany.vatNumber,
            vatAttachment: buyer.buyerCompany.vatAttachment,
            businessNumber: buyer.buyerCompany.businessNumber,
            businessAttachment: buyer.buyerCompany.vatAttachment,
            certificateStatus: buyer.buyerCompany.certificateStatus,
            rejectedReason: buyer.buyerCompany.rejectedReason,
          })
        );
      });
    });
  }

  /**
   * cart에서 사용될 buyer 정보
   * @returns BuyerCompany{vatNumber, vatAttachment, businessNumber, businessAttachment, certificateStatus}
   */
  async wantBuyUdirect(): Promise<BuyerCompany> {
    return new Promise((res, rej) => {
      // VAT번호, VAT첨부파일, Business번호, Business첨부파일
      this.buyerService.getBuyer().subscribe((buyer) => {
        res(
          new BuyerCompany({
            vatNumber: buyer.buyerCompany.vatNumber,
            vatAttachment: buyer.buyerCompany.vatAttachment,
            businessNumber: buyer.buyerCompany.businessNumber,
            businessAttachment: buyer.buyerCompany.vatAttachment,
            certificateStatus: buyer.buyerCompany.certificateStatus,
          })
        );
      });
    });
  }
}
